/**
 * TagCell — compact tag renderer for table rows.
 *
 * Shows the first `maxVisible` chips plus a "+N" overflow badge; clicking the
 * cell (when editable) opens a TagPicker anchored to it. Pair with
 * useEntityTags so a whole page of rows loads tags in one request:
 *
 *   const entityTags = useEntityTags('tracker_piping', rowIds, { projectId });
 *   <TagCell
 *     entityType="tracker_piping" entityId={row.id} projectId={projectId}
 *     tags={entityTags.tagsByEntity[String(row.id)] || []}
 *     onAssign={(tag) => entityTags.assign(tag, row.id)}
 *     onUnassign={(tag) => entityTags.unassign(tag, row.id)}
 *   />
 */

import React, { useState, useRef } from 'react';
import TagChip from './TagChip';
import TagPicker from './TagPicker';
import TagUsageModal from './TagUsageModal';

const TagCell = ({
  entityType,
  entityId,
  projectId,
  tags = [],
  onAssign,
  onUnassign,
  canEdit = true,
  maxVisible = 2,
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [anchorRect, setAnchorRect] = useState(null);
  const [usageTagId, setUsageTagId] = useState(null);
  const cellRef = useRef(null);

  const openPicker = (e) => {
    if (!canEdit) return;
    e.stopPropagation();
    setAnchorRect(cellRef.current?.getBoundingClientRect() || null);
    setPickerOpen(true);
  };

  const visible = tags.slice(0, maxVisible);
  const overflow = tags.length - visible.length;

  return (
    <>
      <div
        ref={cellRef}
        onClick={openPicker}
        title={canEdit ? 'Click to edit tags' : undefined}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 3,
          minWidth: 26,
          minHeight: 18,
          cursor: canEdit ? 'pointer' : 'default',
          borderRadius: 6,
          padding: '1px 2px',
        }}
      >
        {visible.map((tag) => (
          <TagChip key={tag.id} tag={tag} />
        ))}
        {overflow > 0 && (
          <span
            title={tags.slice(maxVisible).map((t) => t.name).join(', ')}
            style={{
              backgroundColor: '#e2e8f0', color: '#475569', borderRadius: 999,
              padding: '1px 7px', fontSize: '0.66rem', fontWeight: 700, whiteSpace: 'nowrap',
            }}
          >
            +{overflow}
          </span>
        )}
        {tags.length === 0 && canEdit && (
          <span style={{ color: '#cbd5e1', fontSize: '0.72rem', fontWeight: 600 }}>＋</span>
        )}
      </div>

      {pickerOpen && (
        <TagPicker
          entityType={entityType}
          projectId={projectId}
          selectedTags={tags}
          anchorRect={anchorRect}
          onClose={() => setPickerOpen(false)}
          onToggle={(tag, nowSelected) => {
            if (nowSelected) onAssign?.(tag, entityId);
            else onUnassign?.(tag, entityId);
          }}
          onViewUsage={(tag) => setUsageTagId(tag.id)}
        />
      )}

      {usageTagId != null && (
        <TagUsageModal tagId={usageTagId} onClose={() => setUsageTagId(null)} />
      )}
    </>
  );
};

export default TagCell;
